import { useTranslation } from 'react-i18next';
import { NativeSelect } from '@/components/ui/native-select';
import {
	Tooltip,
	TooltipArrow,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { CoverageValues } from '@/types/app';
import {
	COVERAGE_OPTIONS,
	EMPLOYER_COST_OPTIONS,
	STANDARD_WAITING_PERIOD_VALUES
} from './constants';
import InfoIcon from './InfoIcon';

interface PeriodOptions {
	firstYearFirstHalf?: number[];
	firstYearSecondHalf?: number[];
	secondYearFirstHalf?: number[];
	secondYearSecondHalf?: number[];
}

export interface CoverageFormFieldsProps {
	values: CoverageValues;
	onChange: (field: keyof CoverageValues, value: number) => void;
	fieldErrors: Record<string, string>;
	disabled?: boolean;
	waitingPeriodOptions?: number[];
	employerCostOptions?: number[];
	periodOptions?: PeriodOptions;
	/** When provided, fields that differ from initialValues get a yellow border and feedback text. */
	initialValues?: CoverageValues;
	/** Prefix for input element data-testid attributes (e.g. "coverage-input" or "slst-modal"). */
	testIdPrefix: string;
	/** Prefix for info/error/feedback data-testid attributes. Defaults to testIdPrefix. */
	testIdAuxPrefix?: string;
}

const PERIODS = [
	{
		field: 'coverage_percentage_first_year_first_half' as const,
		periodKey: 'first',
		optionsKey: 'firstYearFirstHalf' as const,
		testIdSuffix: 'first-year-first-half',
		irrelevantAtDays: 130
	},
	{
		field: 'coverage_percentage_first_year_second_half' as const,
		periodKey: 'second',
		optionsKey: 'firstYearSecondHalf' as const,
		testIdSuffix: 'first-year-second-half',
		irrelevantAtDays: 260
	},
	{
		field: 'coverage_percentage_second_year_first_half' as const,
		periodKey: 'third',
		optionsKey: 'secondYearFirstHalf' as const,
		testIdSuffix: 'second-year-first-half',
		irrelevantAtDays: Infinity
	},
	{
		field: 'coverage_percentage_second_year_second_half' as const,
		periodKey: 'fourth',
		optionsKey: 'secondYearSecondHalf' as const,
		testIdSuffix: 'second-year-second-half',
		irrelevantAtDays: Infinity
	}
] as const;

function fieldBorderClass(hasError: boolean, isChanged: boolean): string {
	if (hasError) return 'border-destructive';
	if (isChanged) return 'border-yellow-500';
	return '';
}

const CoverageFormFields = ({
	values,
	onChange,
	fieldErrors,
	disabled = false,
	waitingPeriodOptions = STANDARD_WAITING_PERIOD_VALUES,
	employerCostOptions = EMPLOYER_COST_OPTIONS,
	periodOptions,
	initialValues,
	testIdPrefix,
	testIdAuxPrefix: auxPrefix
}: CoverageFormFieldsProps) => {
	const { t } = useTranslation();
	const aux = auxPrefix ?? testIdPrefix;

	const isChanged = (field: keyof CoverageValues) =>
		initialValues !== undefined && values[field] !== initialValues[field];

	const anyPercentageChanged =
		initialValues !== undefined &&
		PERIODS.some(({ field }) => values[field] !== initialValues[field]);

	return (
		<div>
			{/* Eigen risico / Waiting period */}
			<hr className="mb-4 border-border" />
			<div className="flex flex-col md:flex-row mb-3 gap-3">
				<div className="md:shrink-0">
					<div className="font-semibold mb-1 whitespace-nowrap">
						{t('quotes.settings.coverage.waitingPeriod.label')}
						<InfoIcon
							tooltipContent={t('quotes.settings.coverage.waitingPeriod.tooltip')}
							testId={`${aux}-info-waiting-period`}
							className="text-wrap"
							color="secondary"
						/>
					</div>
					<div className="text-muted-foreground mb-2 md:mb-0">
						{t('quotes.settings.coverage.waitingPeriod.description')}
					</div>
				</div>
				<div className="grow flex justify-end items-start">
					<div className="w-full md:w-80">
						<NativeSelect
							className={cn(
								fieldBorderClass(!!fieldErrors.waiting_period, isChanged('waiting_period'))
							)}
							value={values.waiting_period}
							onChange={(e) => onChange('waiting_period', Number(e.target.value))}
							data-testid={`${testIdPrefix}-waiting-period`}
							disabled={disabled}
						>
							{waitingPeriodOptions.map((opt) => (
								<option key={opt} value={opt}>
									{opt} {t('quotes.settings.coverage.waitingPeriod.unit')}
								</option>
							))}
						</NativeSelect>
						{fieldErrors.waiting_period && (
							<div
								className="text-destructive text-sm mt-1"
								data-testid={`${aux}-error-waiting-period`}
							>
								{fieldErrors.waiting_period}
							</div>
						)}
						{!fieldErrors.waiting_period && isChanged('waiting_period') && (
							<div
								className="text-xs px-1 py-2 text-muted-foreground"
								data-testid={`${aux}-feedback-waiting-period`}
							>
								Anders dan de herkende dekking op je huidige polis
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Werkgeverslasten / Employer costs */}
			<hr className="my-6 lg:my-4 border-border" />
			<div className="flex flex-col md:flex-row mb-3 gap-3">
				<div className="md:shrink-0">
					<div className="font-semibold mb-1 whitespace-nowrap">
						{t('quotes.settings.coverage.employerCosts.label')}
						<InfoIcon
							tooltipContent={t('quotes.settings.coverage.employerCosts.tooltip')}
							testId={`${aux}-info-employer-costs`}
							className="text-wrap"
							color="secondary"
						/>
					</div>
					<div className="text-muted-foreground mb-2 md:mb-0">
						{t('quotes.settings.coverage.employerCosts.description')}
					</div>
				</div>
				<div className="grow flex justify-end items-start">
					<div className="w-full md:w-80">
						<NativeSelect
							className={cn(
								fieldBorderClass(!!fieldErrors.employer_costs, isChanged('employer_costs'))
							)}
							value={values.employer_costs}
							onChange={(e) => onChange('employer_costs', Number(e.target.value))}
							data-testid={`${testIdPrefix}-employer-costs`}
							disabled={disabled}
						>
							{employerCostOptions.map((opt) => (
								<option key={opt} value={opt}>
									{opt === 0
										? t('quotes.settings.coverage.employerCosts.none')
										: t('quotes.settings.coverage.employerCosts.percentage', {
												percentage: opt
											})}
								</option>
							))}
						</NativeSelect>
						{fieldErrors.employer_costs && (
							<div
								className="text-destructive text-sm mt-1"
								data-testid={`${aux}-error-employer-costs`}
							>
								{fieldErrors.employer_costs}
							</div>
						)}
						{!fieldErrors.employer_costs && isChanged('employer_costs') && (
							<div
								className="text-xs px-1 py-2 text-muted-foreground"
								data-testid={`${aux}-feedback-employer-costs`}
							>
								Anders dan de herkende dekking op je huidige polis
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Vergoeding bij verzuim / Coverage percentages */}
			<hr className="my-6 lg:my-4 border-border" />
			<div className="flex flex-col md:flex-row mb-4 pt-2">
				<div className="pr-2">
					<div className="font-semibold mb-1 whitespace-nowrap">
						{t('quotes.settings.coverage.sicknessBenefit.label')}
						<InfoIcon
							tooltipContent={t('quotes.settings.coverage.sicknessBenefit.tooltip')}
							testId={`${aux}-info-sickness-benefit`}
							className="text-wrap"
							color="secondary"
						/>
					</div>
					<div className="text-muted-foreground mb-2 md:mb-0">
						{t('quotes.settings.coverage.sicknessBenefit.description')}
					</div>
				</div>
				<div className="mt-4 md:mt-0 ml-auto relative w-full md:w-[430px]">
					{/* Timeline Bar */}
					<div className="absolute w-full mb-3 top-7 h-1">
						<div className="h-full w-full rounded-full bg-black" />
						{[0, 25, 50, 75, 100].map((position) => (
							<div
								key={position}
								className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-black"
								style={{ left: `${position}%` }}
							/>
						))}
					</div>

					{/* Period Selectors */}
					<div className="grid grid-cols-4 gap-0 2xl:gap-3 justify-center px-2">
						{PERIODS.map(({ field, periodKey, optionsKey, testIdSuffix, irrelevantAtDays }) => {
							const irrelevant = values.waiting_period >= irrelevantAtDays;
							const options = periodOptions?.[optionsKey] ?? COVERAGE_OPTIONS;
							const testId = `${testIdPrefix}-${testIdSuffix}`;

							return (
								<div key={field} className="text-center px-1">
									<div className="text-center mb-7">
										<div className="font-semibold whitespace-nowrap text-xs">
											{t('quotes.settings.coverage.sicknessBenefit.unit')}{' '}
											{t(`quotes.settings.coverage.sicknessBenefit.periods.${periodKey}`)}
										</div>
									</div>
									<TooltipProvider>
										<Tooltip open={irrelevant ? undefined : false}>
											<TooltipTrigger asChild>
												<div className="w-full">
													<NativeSelect
														className={cn(
															'w-full h-9',
															irrelevant
																? 'bg-muted text-muted-foreground opacity-60'
																: fieldBorderClass(!!fieldErrors.coverage_all, isChanged(field))
														)}
														value={values[field]}
														onChange={(e) => onChange(field, Number(e.target.value))}
														disabled={disabled || irrelevant}
														data-testid={testId}
													>
														{options.map((opt) => (
															<option key={opt} value={opt}>
																{irrelevant
																	? t('quotes.settings.coverage.sicknessBenefit.notApplicable')
																	: `${opt}%`}
															</option>
														))}
													</NativeSelect>
												</div>
											</TooltipTrigger>
											{irrelevant && (
												<TooltipContent>
													<p>
														{t('quotes.settings.coverage.sicknessBenefit.periodNotApplicable', {
															waitingDays: values.waiting_period
														})}
													</p>
													<TooltipArrow />
												</TooltipContent>
											)}
										</Tooltip>
									</TooltipProvider>
								</div>
							);
						})}
					</div>
					{fieldErrors.coverage_all && (
						<div
							className="text-destructive text-sm text-center mt-3"
							data-testid={`${aux}-error-percentages`}
						>
							{fieldErrors.coverage_all}
						</div>
					)}
					{!fieldErrors.coverage_all && anyPercentageChanged && (
						<div
							className="text-xs pt-3 text-center text-muted-foreground"
							data-testid={`${aux}-feedback-percentages`}
						>
							Anders dan de herkende dekking op je huidige polis
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default CoverageFormFields;
